import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { format, isSameDay } from "date-fns";
import classNames from "classnames";
import { CircleCheck, Info } from "lucide-react";

interface WeekViewProps {
  weekDays: any[];
  times: string[];
  statusMap: Record<string, any>;
  sessionDuration?: number;
  handleCellClick: (_date: Date, _time: string) => void;
  handleSlotClick: (_slotId: string) => void;
  isCellClickable: (_date: Date, _time: string) => boolean;
  setSelectedAppointment: (_appointment: any) => void;
  setShowAppointmentDialog: (_show: boolean) => void;
}

const MappingClassByStatus = {
  disabled: {
    background: "bg-[#e0e0e0]",
    opacity: "opacity-50",
  },
  pending: {
    background: "bg-orange-50 border-orange-300 border-2",
    text: "overflow-hidden text-orange-500",
  },
  approved: {
    background: "bg-green-50 border-green-300 border-2",
    text: "text-green-500",
  },
  available: {
    background: "bg-white",
  },
};

interface IAppointmentInfoProps {
  appointment: any;
  title: string;
  startTime: string;
  endTime: string;
  status: "pending" | "approved";
  isDisabled?: boolean;
  setSelectedAppointment: (_appointment?: any) => void;
  setShowAppointmentDialog: (_show: boolean) => void;
}

const AppointmentInfo: React.FC<IAppointmentInfoProps> = ({
  appointment,
  title,
  startTime,
  endTime,
  status,
  isDisabled,
  setSelectedAppointment,
  setShowAppointmentDialog,
}) => {
  const tooltipText = `${format(new Date(startTime), "HH:mm dd/MM/yyyy")} - ${format(new Date(endTime), "HH:mm dd/MM/yyyy")}\n${title}`;
  return (
    <Box
      key={startTime}
      className={classNames(
        `border border-gray-200 h-[50px] rounded relative`,
        {
          "opacity-50 cursor-not-allowed": isDisabled,
          "cursor-pointer": !isDisabled,
          [MappingClassByStatus.disabled.opacity]: isDisabled,
          [MappingClassByStatus.disabled.background]: isDisabled,
        },
        MappingClassByStatus[status]?.background ?? ""
      )}
    >
      <Tooltip title={tooltipText} className="absolute top-0 left-0">
        <Box
          className={classNames(
            "absolute inset-x-0 rounded text-xs p-2 z-10 flex flex-col justify-center",
            {
              "opacity-50 cursor-not-allowed": isDisabled,
              "overflow-hidden font-medium min-h-[48px] top-0": true,
            },
            MappingClassByStatus[status].text
          )}
          sx={{
            height: "50px",
            fontSize: "0.875rem",
            textAlign: "left",
            overflow: "hidden",
            maxWidth: "100%",
          }}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            setSelectedAppointment(appointment);
            setShowAppointmentDialog(true);
          }}
        >
          <div className="flex w-full">
            <div className="flex flex-col flex-1">
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "140px",
                  display: "inline-block",
                  fontWeight: "600",
                }}
                title={title}
              >
                {title}
              </span>
              <p
                className="text-xs mt-1 line-clamp-1"
                title={appointment.description || "Individual Therapy"}
              >
                {appointment.description || "Individual Therapy"}
              </p>
            </div>

            <div className="flex items-center justify-center ml-2">
              {status === "approved" ? (
                <CircleCheck size={20} className="text-white fill-green-600" />
              ) : (
                <Info
                  size={20}
                  className="text-white fill-orange-600 rotate-180"
                />
              )}
            </div>
          </div>
        </Box>
      </Tooltip>
    </Box>
  );
};

interface ISlotProps {
  slotId?: string;
  startTime?: string;
  endTime?: string;
  sessionDuration: number;
  isDisabled?: boolean;
  isAvailable?: boolean;
  onClick: (_slotId: string) => void;
}

const Slot: React.FC<ISlotProps> = ({
  slotId,
  onClick,
  isAvailable = true,
  startTime,
  sessionDuration: _sessionDuration,
  isDisabled = false,
}) => {
  return (
    <Box
      key={startTime}
      className={classNames(
        `border border-gray-200 h-[50px] rounded relative`,
        {
          "opacity-50 cursor-not-allowed": isDisabled,
          "cursor-pointer": !isDisabled,
          [MappingClassByStatus.disabled.opacity]: isDisabled,
          [MappingClassByStatus.disabled.background]: !slotId || isDisabled,
        }
      )}
    >
      {slotId && (
        <Box
          className={classNames(
            "absolute inset-x-0 rounded text-xs p-1 z-10",
            {
              "opacity-50 cursor-not-allowed": isDisabled,
              "overflow-hidden whitespace-nowrap font-medium min-h-[48px] top-0": true,
              "flex items-center justify-center": true,
            },
            { [MappingClassByStatus.available.background]: isAvailable }
          )}
          sx={{
            height: "50px",
          }}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            slotId && onClick(slotId);
          }}
        ></Box>
      )}
    </Box>
  );
};

const WeekView: React.FC<WeekViewProps> = ({
  weekDays,
  statusMap,
  handleSlotClick,
  setSelectedAppointment,
  setShowAppointmentDialog,
  sessionDuration = 30,
  times,
}) => {
  const [currentTime, setCurrentTime] = React.useState(new Date());

  // Update current time every 30 seconds
  React.useEffect(() => {
    setCurrentTime(new Date());

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Calculate current time position
  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Convert current time to minutes since midnight
    const currentTotalMinutes = hours * 60 + minutes;

    // If no time slots are defined, return 0
    if (!times || times.length === 0) return 0;

    // Convert time slots to minutes for easier comparison
    const timeSlots = times.map((time) => {
      const [h, m] = time.split(":").map(Number);
      return { time, minutes: h * 60 + m };
    });

    // Direct check against the time slots in our array
    for (let i = 0; i < timeSlots.length; i++) {
      const time = timeSlots[i].time;
      const [slotHours, slotMinutes] = time.split(":").map(Number);

      // Direct match to a time slot
      if (hours === slotHours && minutes === slotMinutes) {
        return i;
      }
    }

    // Case 1: Before the first slot
    if (currentTotalMinutes < timeSlots[0].minutes) {
      return 0;
    }

    // Case 2: After the last slot
    if (currentTotalMinutes >= timeSlots[timeSlots.length - 1].minutes) {
      return timeSlots.length - 1;
    }

    // Case 3: Between slots - find which slots we're between
    for (let i = 0; i < timeSlots.length - 1; i++) {
      const currentSlot = timeSlots[i];
      const nextSlot = timeSlots[i + 1];

      if (
        currentTotalMinutes >= currentSlot.minutes &&
        currentTotalMinutes < nextSlot.minutes
      ) {
        // Calculate how far between these slots we are (0.0 to 1.0)
        const slotDuration = nextSlot.minutes - currentSlot.minutes;
        const minutesIntoSlot = currentTotalMinutes - currentSlot.minutes;
        const progress = minutesIntoSlot / slotDuration;

        return i + progress;
      }
    }

    // Fallback
    return 0;
  };

  // Calculate position each time component renders
  const currentTimePosition = React.useMemo(() => {
    const position = getCurrentTimePosition();
    return position;
  }, [currentTime, times]);

  // Find today's index in the week days array
  const now = new Date();
  const todayIndex = weekDays.findIndex((day) => isSameDay(day.date, now));

  // Only show timeline if we have a valid position and today is in the visible week
  const showCurrentTimeLine =
    currentTimePosition >= 0 &&
    currentTimePosition <= times.length &&
    todayIndex !== -1;

  return (
    <Box className="hidden md:block relative" id="weekview-container">
      <Box
        className="grid"
        style={{ gridTemplateColumns: "100px repeat(7, 1fr)" }}
      >
        <Box></Box>
        {weekDays.map((day) => (
          <Box
            key={day.fullDate}
            className="text-center font-bold p-2 border-b bg-gray-100"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: isSameDay(day.date, new Date()) ? "#ff9800" : "inherit",
                fontWeight: isSameDay(day.date, new Date()) ? 700 : 600,
              }}
            >
              {day.dayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                fontWeight: 400,
                color: isSameDay(day.date, new Date()) ? "#ff9800" : "inherit",
              }}
            >
              {day.displayDate}
            </Typography>
          </Box>
        ))}
        {times.map((time) => (
          <Box
            component="div"
            key={time}
            sx={{ display: "contents", position: "relative" }}
          >
            <Typography
              variant="body2"
              className="border border-gray-200 p-2 text-base text-right pr-3 font-medium relative"
            >
              {time}
            </Typography>
            {weekDays.map((day) => {
              const key = `${day.fullDate} ${time}`;
              const item = statusMap[key];
              const now = new Date();
              const isPastSlot = item ? new Date(item?.startTime) < now : false;
              const isUnavailable = item ? item?.status !== "available" : false;

              const startDate = `${day.fullDate}T${time}`;
              const endDateObj = new Date(startDate);
              endDateObj.setMinutes(endDateObj.getMinutes() + sessionDuration);
              const endDate = format(endDateObj, "yyyy-MM-dd'T'HH:mm");

              if (item?.appointmentId) {
                // Approved appointments are always clickable and not blurred
                const isApproved = item.status === "approved";
                let isDisabled = false;
                if (!isApproved) {
                  isDisabled = isPastSlot;
                }
                return (
                  <AppointmentInfo
                    title={item.title}
                    key={item.appointmentId}
                    appointment={item}
                    startTime={startDate}
                    endTime={endDate}
                    status={item.status}
                    setSelectedAppointment={setSelectedAppointment}
                    setShowAppointmentDialog={setShowAppointmentDialog}
                    isDisabled={isDisabled}
                  />
                );
              }
              const isDisabled = isPastSlot || isUnavailable;
              return (
                <Slot
                  onClick={() => handleSlotClick(item?.slotId)}
                  key={key}
                  slotId={item?.slotId}
                  startTime={startDate}
                  endTime={endDate}
                  sessionDuration={sessionDuration}
                  isAvailable={item?.status === "available"}
                  isDisabled={isDisabled || isPastSlot}
                />
              );
            })}
          </Box>
        ))}

        {showCurrentTimeLine &&
          weekDays.map(
            (day, index) =>
              isSameDay(day.date, now) && (
                <Box
                  key={`timeline-${day.fullDate}-${now.getTime()}`}
                  className="absolute z-20"
                  sx={{
                    top: `${
                      47 + // Starting position
                      50 * Math.floor(currentTimePosition) + // Position to the start of current row
                      50 * (currentTimePosition % 1) + // Fine position within the row
                      19 // Offset adjustment for better alignment
                    }px`,
                    height: "2px",
                    backgroundColor: "red",
                    left: `calc(100px + (${index} * ((100% - 100px) / 7)))`,
                    width: `calc((100% - 100px) / 7)`,
                    transform: `translateY(-50%)`,
                    transition: "top 0.3s ease",
                  }}
                >
                  <div className="absolute -left-1 -top-1 w-3 h-3 rounded-full bg-red-600"></div>
                </Box>
              )
          )}
      </Box>
    </Box>
  );
};

export default React.memo(WeekView);
